import React, { useEffect, useState } from "react";
import { Plus, X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import comparativesApi from "@/api/comparatives";

const ALL_TX = ["RF PICK", "RF STAGE", "RF TRNS", "RF.RCVR", "RF RCPT"];
const MAX_TYPES = 5;

const DEFAULT_CONFIG = {
  types: ["RF PICK", "RF STAGE"],
  metric: "count",
  granularity: "month",
  breakdown: "total",
  filters: { preset: "year" },
};

/**
 * Phase 5.4 — Builder modal. Configures a comparative (1..5 transaction
 * types) and saves it via POST/PATCH /api/comparatives.
 *
 * If `editing` is provided we PATCH it. Otherwise POST a new one.
 */
export default function ComparativeBuilder({ open, onClose, editing, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && editing) {
      setName(editing.name || "");
      setDescription(editing.description || "");
      setConfig({
        types: editing.config?.types || ["RF PICK", "RF STAGE"],
        metric: editing.config?.metric || "count",
        granularity: editing.config?.granularity || "month",
        breakdown: editing.config?.breakdown || "total",
        filters: editing.config?.filters || { preset: "year" },
      });
    } else if (open && !editing) {
      setName("");
      setDescription("");
      setConfig(DEFAULT_CONFIG);
    }
  }, [open, editing]);

  const addType = (t) => {
    if (config.types.includes(t)) return;
    if (config.types.length >= MAX_TYPES) {
      toast.error(`Máximo ${MAX_TYPES} tipos por comparativo.`);
      return;
    }
    setConfig({ ...config, types: [...config.types, t] });
  };

  const removeType = (t) => {
    if (config.types.length <= 1) {
      toast.error("Se necesita al menos 1 tipo.");
      return;
    }
    setConfig({ ...config, types: config.types.filter((x) => x !== t) });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Pon un nombre al comparativo.");
      return;
    }
    if (config.types.length < 1) {
      toast.error("Selecciona al menos 1 tipo.");
      return;
    }
    setSaving(true);
    try {
      const body = { name, description, config };
      let r;
      if (editing) {
        r = await comparativesApi.update(editing.id, body);
      } else {
        r = await comparativesApi.create(body);
      }
      toast.success(editing ? "Comparativo actualizado" : "Comparativo creado");
      onSaved?.(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error guardando";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const availableTypes = ALL_TX.filter((t) => !config.types.includes(t));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="comparative-builder">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Editar comparativo" : "Nuevo comparativo"}
          </DialogTitle>
          <DialogDescription>
            Compara 1 a {MAX_TYPES} tipos de transacción. Guarda la configuración
            para reutilizarla.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="cb-name" className="text-xs">Nombre</Label>
            <Input
              id="cb-name"
              data-testid="builder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. PICK vs STAGE diario"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="cb-desc" className="text-xs">Descripción (opcional)</Label>
            <Textarea
              id="cb-desc"
              data-testid="builder-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Por qué te importa este comparativo…"
              rows={2}
            />
          </div>

          {/* Types */}
          <div className="space-y-1">
            <Label className="text-xs">
              Tipos de transacción ({config.types.length}/{MAX_TYPES})
            </Label>
            <div
              className="flex flex-wrap gap-1.5 rounded-md border border-border bg-muted/40 p-2"
              data-testid="builder-types"
            >
              {config.types.map((t) => (
                <Badge
                  key={t}
                  variant="secondary"
                  className="gap-1 font-mono text-xs"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeType(t)}
                    data-testid={`remove-type-${t}`}
                    className="rounded-sm hover:bg-foreground/10"
                    aria-label={`Remove ${t}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {config.types.length === 0 && (
                <span className="text-[10px] text-muted-foreground">
                  Selecciona un tipo abajo…
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {availableTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => addType(t)}
                  data-testid={`add-type-${t}`}
                  className="inline-flex items-center gap-1 rounded border border-dashed border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-primary"
                >
                  <Plus className="h-3 w-3" />
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Metric + Granularity + Breakdown */}
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Métrica</Label>
              <Select
                value={config.metric}
                onValueChange={(v) => setConfig({ ...config, metric: v })}
              >
                <SelectTrigger data-testid="builder-metric" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Count</SelectItem>
                  <SelectItem value="tx_per_hour">tx/hr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Granularidad</Label>
              <Select
                value={config.granularity}
                onValueChange={(v) => setConfig({ ...config, granularity: v })}
              >
                <SelectTrigger data-testid="builder-granularity" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Día</SelectItem>
                  <SelectItem value="week">Semana</SelectItem>
                  <SelectItem value="month">Mes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Desglose</Label>
              <Select
                value={config.breakdown}
                onValueChange={(v) => setConfig({ ...config, breakdown: v })}
              >
                <SelectTrigger data-testid="builder-breakdown" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">Total</SelectItem>
                  <SelectItem value="by_employee">Por empleado</SelectItem>
                  <SelectItem value="by_department">Por depto.</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preset window */}
          <div className="space-y-1">
            <Label className="text-xs">Periodo (preset)</Label>
            <Select
              value={config.filters?.preset || "year"}
              onValueChange={(v) =>
                setConfig({
                  ...config,
                  filters: { ...(config.filters || {}), preset: v },
                })
              }
            >
              <SelectTrigger data-testid="builder-preset" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hoy</SelectItem>
                <SelectItem value="week">Esta semana</SelectItem>
                <SelectItem value="month">Este mes</SelectItem>
                <SelectItem value="year">Este año</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="builder-save">
            {saving ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {editing ? "Guardar cambios" : "Crear y ejecutar"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
